/**
 * SW Exporter 로 뽑은 계정 JSON → **보유몹 개수**.
 *
 * SWEX 가 저장하는 파일은 게임의 `HubUserLogin` 응답 그대로다(예: `애플톡-2840241.json`,
 * 7MB 남짓).
 *
 *   unit_master_id  →  매핑에서 (가문, 속성)
 *   →  콜라보 짝은 이름 앞선 쪽으로 열쇠 고정  →  태생 4~5성만  →  같은 열쇠끼리 마리 수 합
 *
 * **몹이 세 곳에 나뉘어 있다.**
 *   `unit_list`                 몬스터 보관소(늘 데리고 있는 몹)
 *   `unit_storage_normal_list`  봉인소의 **일반 봉인**
 *   `unit_storage_list`         봉인소의 **소환 봉인**
 * 함께 세지 않으면 봉인해 둔 몹이 빠진다. 실측(애플톡 계정)으로 일반 봉인에 39마리가
 * 있었고 그중 7마리가 태생 4~5성이었다.
 *
 * **소환 봉인은 로그인 응답에 안 들어 있다.** 게임이 봉인소 화면을 열 때 따로 보내는
 * `GetUnitStorageList`(요즘은 `GetWizardDataPart1` 안에 들어 온다) 응답에만 있다. SWEX 에도
 * 이걸 계정 파일에 합쳐 주는 설정(`Merge sealed monster storage`)이 있지만 라벨에 대놓고
 * "Currently not working" 이라고 적혀 있어서, 우리 플러그인(`swex-plugin/swpvpdb-uploader.js`)
 * 이 대신 합쳐 넣는다. 그래서 소환 봉인까지 세려면 **게임에서 봉인소를 한 번 열고** 파일을
 * 올려야 한다 — 목록이 비어 있으면 화면이 그렇게 알려 준다.
 *
 * **브라우저에서 읽는다.** 7MB 를 서버로 올릴 이유가 없고, 열쇠를 만드는 규칙(콜라보 짝을
 * 어느 이름으로 모으는지)은 이미 화면이 알고 있다. 서버로는 세어 놓은 개수만 보낸다.
 *
 * `unit_collection`(도감)이 아니라 실제 보유 목록을 센다 — 도감은 "한 번이라도 가져 본
 * 몹"이라 지금 없는 몹까지 들어 있다. `draft_unit_list`(뽑기 이벤트)도 내 몹이 아니다.
 *
 * 실측(애플톡 계정, 720마리): 태생 4~5성 545마리 → 374칸, 매핑에 없는 id 0개.
 */

const ATTRS = ['Water', 'Fire', 'Wind', 'Light', 'Dark'];
const STATES = ['Unawaken', 'Awakened', 'SecondAwakened'];

/** 몹이 들어 있는 목록들. 없는 목록은 건너뛴다. */
const UNIT_LISTS: { key: string; kind: 'main' | 'normalSeal' | 'summonSeal' }[] = [
    { key: 'unit_list', kind: 'main' },
    { key: 'unit_storage_normal_list', kind: 'normalSeal' },
    { key: 'unit_storage_list', kind: 'summonSeal' }
];

export interface SwexRosterResult {
    /** `가문|속성` → 마리 수 */
    counts: Record<string, number>;
    /** 계정에 있던 몹 전체 (보관소 + 봉인소) */
    total: number;
    /** 일반 봉인에 있던 몹 수 */
    sealedNormal: number;
    /** 소환 봉인에 있던 몹 수 */
    sealedSummon: number;
    /**
     * 소환 봉인 목록이 파일에 **있었는지**.
     *
     * 0마리인 것과 목록 자체가 없는 것은 다르다 — 없으면 세지 못한 것이므로 화면이
     * "봉인소를 열고 다시 뽑으세요"라고 알려 줘야 한다.
     */
    hasSummonSeal: boolean;
    /** 태생 4~5성으로 잡힌 마리 수 */
    matched: number;
    /** 태생 3성 이하라 뺀 마리 수 */
    lowStar: number;
    /** 매핑에 없는 몹 (새 몹이면 몬스터 동기화가 필요하다) */
    unknown: number;
    /** 계정 이름 — 파일이 정말 그 사람 것인지 확인하는 데 쓴다 */
    wizardName: string | null;
}

/**
 * `unit_master_id` → (가문, 속성, 태생 성급).
 *
 * 각성 전·후·2차각성이 서로 다른 id 를 갖지만 **같은 (가문, 속성)** 이다. 그래서 각성한
 * 몹과 안 한 몹을 따로 세지 않고 한 칸으로 합친다.
 */
export function buildIdIndex(mapping: any): Map<number, { family: string; attr: string; natural: number | null }> {
    const out = new Map<number, { family: string; attr: string; natural: number | null }>();
    for (const [family, info] of Object.entries<any>(mapping?.monsters || {})) {
        for (const attr of ATTRS) {
            const byAttr = info?.[attr];
            if (!byAttr) continue;
            const un = byAttr.Unawaken?.stars;
            const aw = byAttr.Awakened?.stars;
            const natural = typeof un === 'number' ? un : (typeof aw === 'number' ? aw - 1 : null);
            for (const st of STATES) {
                const raw = byAttr?.[st]?.unit_master_id;
                for (const q of Array.isArray(raw) ? raw : [raw]) {
                    const id = Number(q);
                    if (Number.isFinite(id)) out.set(id, { family, attr, natural });
                }
            }
        }
    }
    return out;
}

/** 콜라보 짝은 이름이 앞선 쪽으로 열쇠를 고정한다 (화면·서버와 같은 규칙) */
function canonicalFamily(mapping: any, family: string): string {
    const partner = mapping?.monsters?.[family]?.collab;
    if (!partner || !mapping?.monsters?.[partner]) return family;
    return family <= partner ? family : partner;
}

/**
 * SWEX 계정 JSON 을 읽어 개수를 센다.
 *
 * @throws 파일이 SWEX 계정 JSON 이 아니면 (`unit_list` 가 없으면)
 */
export function countFromSwex(account: any, mapping: any): SwexRosterResult {
    if (!Array.isArray(account?.unit_list)) {
        throw new Error('SWEX 계정 파일이 아닙니다 (unit_list 가 없습니다)');
    }

    const index = buildIdIndex(mapping);
    const counts: Record<string, number> = {};
    let total = 0;
    let sealedNormal = 0;
    let sealedSummon = 0;
    let hasSummonSeal = false;
    let matched = 0;
    let lowStar = 0;
    let unknown = 0;

    for (const { key: listName, kind } of UNIT_LISTS) {
        const units = account?.[listName];
        if (!Array.isArray(units)) continue;
        total += units.length;
        if (kind === 'normalSeal') sealedNormal += units.length;
        if (kind === 'summonSeal') { sealedSummon += units.length; hasSummonSeal = true; }

        for (const u of units) {
            const id = Number(u?.unit_master_id);
            const e = index.get(id);
            if (!e) { unknown += 1; continue; }
            if (e.natural !== 4 && e.natural !== 5) { lowStar += 1; continue; }
            const key = `${canonicalFamily(mapping, e.family)}|${e.attr}`;
            counts[key] = (counts[key] || 0) + 1;
            matched += 1;
        }
    }

    return {
        counts,
        total,
        sealedNormal,
        sealedSummon,
        hasSummonSeal,
        matched,
        lowStar,
        unknown,
        wizardName: account?.wizard_info?.wizard_name || null
    };
}
